import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Utensils, Plus, CheckCircle, Calendar, Edit, Save, X, Trash2,
  ChefHat, AlertTriangle, Search, Pill
} from 'lucide-react';
import { MealEntry, MealSlot, MealCategory, Recipe, DatabaseSchema } from '../types';
import { format } from 'date-fns';
import { WeekNavigation } from './WeekNavigation';
import { getWeekDays } from '../utils/weekUtils';
import { useToast } from './shared/ToastProvider';
import {
  getMealSlotsForDate,
  getMealSlotLabel,
  getMealSlotColor,
  calculateDailyProtein,
  calculateDailyCalories,
  getCurrentPhase,
  getDayMode,
  getDailyProteinTarget,
  getDailyCalorieTarget,
  getCompletedSlotsCount,
  ALL_RECIPES,
  PROTEIN_REFERENCE,
  NON_NEGOTIABLES,
  NUTRITION_PHASES,
} from '../utils/nutritionPlan';

interface MealPlannerProps {
  database: DatabaseSchema | null;
  onAddMeal: (meal: MealEntry) => Promise<void>;
  onDeleteMeal?: (id: string) => Promise<void>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`meal-tabpanel-${index}`}
      aria-labelledby={`meal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export const MealPlanner: React.FC<MealPlannerProps> = ({ database, onAddMeal, onDeleteMeal }) => {
  const toast = useToast();
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [editingDay, setEditingDay] = useState<{ dateString: string; slot: MealSlot } | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editProtein, setEditProtein] = useState('');
  const [editCalories, setEditCalories] = useState('');
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [showNonNegotiables, setShowNonNegotiables] = useState(true);
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>(null);

  // Quick entry state
  const [qeDescription, setQeDescription] = useState('');
  const [qeSlot, setQeSlot] = useState<MealSlot>('breakfast');
  const [qeProtein, setQeProtein] = useState('');
  const [qeCalories, setQeCalories] = useState('');
  const [qeLoading, setQeLoading] = useState(false);
  // Recipe search/filter state
  const [recipeSearch, setRecipeSearch] = useState('');
  const [recipeCategoryFilter, setRecipeCategoryFilter] = useState<MealCategory[]>([]);
  const [recipeTagFilter, setRecipeTagFilter] = useState<string[]>([]);
  const [recipePrepTimeMax, setRecipePrepTimeMax] = useState<number | null>(null);
  const [recipeSortBy, setRecipeSortBy] = useState<'protein' | 'prepTime' | 'name' | 'calories'>('name');

  // Recipe use slot selection
  const [recipeSlotPicker, setRecipeSlotPicker] = useState<{ recipe: Recipe; slots: MealSlot[] } | null>(null);

  const meals = database?.meals || [];
  const preferences = database?.preferences;
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  const todayMeals = meals.filter(m => m.date === todayStr);
  const dayMode = getDayMode(today);
  const todaySlots = getMealSlotsForDate(today);
  const phase = getCurrentPhase(preferences?.nutritionPhaseStartDate);
  const proteinTarget = getDailyProteinTarget(phase);
  const calorieTarget = getDailyCalorieTarget(phase, dayMode);
  const dailyProtein = calculateDailyProtein(meals, todayStr);
  const dailyCalories = calculateDailyCalories(meals, todayStr);
  const completedCount = getCompletedSlotsCount(meals, todayStr);

  // --- Handlers ---

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleToggleCompleted = async (meal: MealEntry) => {
    try {
      await onAddMeal({ ...meal, completed: !meal.completed });
      toast.success(meal.completed ? 'Meal marked as incomplete.' : 'Meal marked as done!');
    } catch (error) {
      console.error('Error updating meal:', error);
      toast.error('Failed to update meal.');
    }
  };

  const handleDeleteMeal = async (meal: MealEntry) => {
    if (!onDeleteMeal) return;
    const confirmDelete = window.confirm(`Delete ${getMealSlotLabel(meal.mealType)} from ${format(new Date(meal.date + 'T12:00:00'), 'MMM dd, yyyy')}?\n\n"${meal.description}"`);
    if (confirmDelete) {
      await onDeleteMeal(meal.id);
    }
  };

  const openSlotEditor = (dateString: string, slot: MealSlot, existingMeal?: MealEntry) => {
    setEditingDay({ dateString, slot });
    setEditDescription(existingMeal?.description || '');
    setEditProtein(existingMeal?.protein?.toString() || '');
    setEditCalories(existingMeal?.calories?.toString() || '');
  };

  const handleSaveSlotMeal = async () => {
    if (!editDescription || !editingDay) return;
    setQuickAddLoading(true);
    try {
      const existingMeal = meals.find(
        m => m.date === editingDay.dateString && m.mealType === editingDay.slot
      );
      const newMeal: MealEntry = {
        id: existingMeal?.id || `meal_${Date.now()}`,
        date: editingDay.dateString,
        mealType: editingDay.slot,
        description: editDescription,
        protein: editProtein ? parseFloat(editProtein) : undefined,
        calories: editCalories ? parseFloat(editCalories) : undefined,
        completed: existingMeal?.completed || false,
      };
      await onAddMeal(newMeal);
      toast.success('Meal saved!');
      setEditingDay(null);
      setEditDescription('');
      setEditProtein('');
      setEditCalories('');
    } catch (error) {
      console.error('Error saving meal:', error);
      toast.error('Failed to save meal.');
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleQuickEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!qeDescription) return;
    setQeLoading(true);
    try {
      const newMeal: MealEntry = {
        id: `meal_${Date.now()}`,
        date: todayStr,
        mealType: qeSlot,
        description: qeDescription,
        protein: qeProtein ? parseFloat(qeProtein) : undefined,
        calories: qeCalories ? parseFloat(qeCalories) : undefined,
        completed: false,
      };
      await onAddMeal(newMeal);
      toast.success('Meal logged!');
      setQeDescription('');
      setQeProtein('');
      setQeCalories('');
    } catch (error) {
      console.error('Error adding meal:', error);
      toast.error('Failed to log meal.');
    } finally {
      setQeLoading(false);
    }
  };

  // Map recipe meal categories to actual MealSlots based on current day mode
  const getCandidateSlots = (recipe: Recipe): MealSlot[] => {
    const slotMap: Record<MealCategory, MealSlot[]> = {
      breakfast: ['breakfast'],
      lunch: ['lunch'],
      dinner: ['dinner'],
      snack: dayMode === 'weekday'
        ? ['mid-morning-snack', 'pre-ride-snack', 'post-ride-snack', 'snack']
        : ['snack'],
    };
    const slots: MealSlot[] = [];
    for (const cat of recipe.mealCategories) {
      for (const s of slotMap[cat]) {
        if (!slots.includes(s)) slots.push(s);
      }
    }
    return slots;
  };

  const handleUseRecipe = (recipe: Recipe) => {
    const candidateSlots = getCandidateSlots(recipe);
    if (candidateSlots.length === 1) {
      const slot = candidateSlots[0];
      const existingMeal = todayMeals.find(m => m.mealType === slot);
      openSlotEditor(todayStr, slot, existingMeal);
      setEditDescription(recipe.name);
      setEditProtein(recipe.protein.toString());
      setEditCalories(recipe.calories.toString());
    } else {
      // Multiple possible slots — show picker
      setRecipeSlotPicker({ recipe, slots: candidateSlots });
    }
  };

  const handlePickSlotForRecipe = (slot: MealSlot) => {
    if (!recipeSlotPicker) return;
    const { recipe } = recipeSlotPicker;
    const existingMeal = todayMeals.find(m => m.mealType === slot);
    openSlotEditor(todayStr, slot, existingMeal);
    setEditDescription(recipe.name);
    setEditProtein(recipe.protein.toString());
    setEditCalories(recipe.calories.toString());
    setRecipeSlotPicker(null);
  };

  // --- Weekly View Data ---
  const weeklyMealData = useMemo(() => {
    const weekDays = getWeekDays(selectedWeek);
    return weekDays.map(day => {
      const dayMeals = meals.filter(m => m.date === day.dateString);
      const dayDate = new Date(day.dateString + 'T12:00:00');
      const mode = getDayMode(dayDate);
      const protein = calculateDailyProtein(meals, day.dateString);
      const calories = calculateDailyCalories(meals, day.dateString);
      const completed = getCompletedSlotsCount(meals, day.dateString);
      const slots = getMealSlotsForDate(dayDate);
      return { ...day, dayMeals, mode, protein, calories, completed, totalSlots: slots.length };
    });
  }, [selectedWeek, meals]);

  // --- Recipe filtering ---
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    ALL_RECIPES.forEach(r => r.tags.forEach(t => tags.add(t)));
    return Array.from(tags).sort();
  }, []);

  const filteredRecipes = useMemo(() => {
    let recipes = ALL_RECIPES;

    // Category filter
    if (recipeCategoryFilter.length > 0) {
      recipes = recipes.filter(r =>
        r.mealCategories.some(c => recipeCategoryFilter.includes(c))
      );
    }

    // Tag filter
    if (recipeTagFilter.length > 0) {
      recipes = recipes.filter(r =>
        recipeTagFilter.some(t => r.tags.includes(t))
      );
    }

    // Prep time filter
    if (recipePrepTimeMax !== null) {
      recipes = recipes.filter(r => r.prepTime <= recipePrepTimeMax);
    }

    // Text search
    if (recipeSearch.trim()) {
      const q = recipeSearch.toLowerCase();
      recipes = recipes.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.ingredients.some(ing => ing.toLowerCase().includes(q))
      );
    }

    // Sort
    switch (recipeSortBy) {
      case 'protein': return [...recipes].sort((a, b) => b.protein - a.protein);
      case 'prepTime': return [...recipes].sort((a, b) => a.prepTime - b.prepTime);
      case 'calories': return [...recipes].sort((a, b) => a.calories - b.calories);
      case 'name':
      default: return [...recipes].sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [recipeSearch, recipeCategoryFilter, recipeTagFilter, recipePrepTimeMax, recipeSortBy]);

  const mainMealCount = todayMeals.filter(m => ['breakfast', 'lunch', 'dinner'].includes(m.mealType)).length;
  void mainMealCount;

  // --- Protein progress color ---
  const getProteinColor = (current: number, min: number) => {
    const ratio = current / min;
    if (ratio >= 0.9) return '#059669';
    if (ratio >= 0.6) return '#d97706';
    return '#ef4444';
  };

  return (
    <Box p={{ xs: 1, sm: 2, md: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Nutrition Tracker
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', mb: 1 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Today" icon={<Utensils size={18} />} iconPosition="start" />
          <Tab label="Weekly View" icon={<Calendar size={18} />} iconPosition="start" />
          <Tab label="Recipes" icon={<ChefHat size={18} />} iconPosition="start" />
          <Tab label="Quick Entry" icon={<Plus size={18} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* ==================== TAB 0: TODAY ==================== */}
      <TabPanel value={currentTab} index={0}>
        {/* Day Mode Banner */}
        <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
          <Chip
            label={dayMode === 'weekday' ? 'Weekday Mode' : 'Weekend Mode'}
            color={dayMode === 'weekday' ? 'primary' : 'secondary'}
            size="small"
          />
          <Chip
            label={phase.name}
            variant="outlined"
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {format(today, 'EEEE, MMM dd')}
          </Typography>
        </Box>

        {/* Daily Summary Bar */}
        <Card sx={{ mb: 3, backgroundColor: '#f8fafc' }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontWeight="bold">
                      Protein
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color={getProteinColor(dailyProtein, proteinTarget.min)}>
                      {dailyProtein}g / {proteinTarget.min}-{proteinTarget.max}g
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((dailyProtein / proteinTarget.min) * 100, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getProteinColor(dailyProtein, proteinTarget.min),
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontWeight="bold">
                      Calories
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {dailyCalories} / ~{calorieTarget} cal
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((dailyCalories / calorieTarget) * 100, 100)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircle size={18} color="#059669" />
                    <Typography variant="body2">
                      <strong>{completedCount}</strong> / {todaySlots.length} meals
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Non-Negotiables (weekday only) */}
        {dayMode === 'weekday' && (
          <Box mb={2}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => setShowNonNegotiables(!showNonNegotiables)}
            >
              <AlertTriangle size={16} color="#d97706" />
              <Typography variant="body2" fontWeight="bold" color="#d97706">
                3 Non-Negotiables
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {showNonNegotiables ? '(hide)' : '(show)'}
              </Typography>
            </Box>
            <Collapse in={showNonNegotiables}>
              <Alert severity="warning" sx={{ mt: 1 }}>
                {NON_NEGOTIABLES.map((rule, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: i < 2 ? 0.5 : 0 }}>
                    <strong>{i + 1}.</strong> {rule}
                  </Typography>
                ))}
              </Alert>
            </Collapse>
          </Box>
        )}

        {/* Weekend reminder */}
        {dayMode === 'weekend' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              No medication today - eat when hungry, keep protein high at each meal. Your hunger signals are reliable.
            </Typography>
          </Alert>
        )}

        {/* Meal Slot Cards */}
        <Grid container spacing={2}>
          {todaySlots.map((slotConfig) => {
            const slotMeal = todayMeals.find(m => m.mealType === slotConfig.slot);
            const slotColor = getMealSlotColor(slotConfig.slot);

            return (
              <Grid item xs={12} sm={6} key={slotConfig.slot}>
                <Card
                  sx={{
                    border: '1px solid',
                    borderColor: slotMeal?.completed ? '#059669' : '#e2e8f0',
                    borderLeft: `4px solid ${slotColor}`,
                    backgroundColor: slotMeal?.completed ? '#f0fdf4' : '#fff',
                    transition: 'all 0.2s',
                  }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Slot Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: slotColor }}>
                          {slotConfig.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {slotConfig.timeWindow} &middot; ~{slotConfig.targetProtein}g protein
                        </Typography>
                      </Box>
                      <Box display="flex" gap={0.5}>
                        {slotMeal && (
                          <IconButton
                            size="small"
                            onClick={() => handleToggleCompleted(slotMeal)}
                            color={slotMeal.completed ? 'success' : 'default'}
                          >
                            <CheckCircle size={18} />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => openSlotEditor(todayStr, slotConfig.slot, slotMeal)}
                        >
                          {slotMeal ? <Edit size={16} /> : <Plus size={16} />}
                        </IconButton>
                        {slotMeal && onDeleteMeal && (
                          <IconButton size="small" onClick={() => handleDeleteMeal(slotMeal)} color="error">
                            <Trash2 size={16} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {/* Alarm warning for mid-morning snack */}
                    {slotConfig.requiresAlarm && !slotMeal && (
                      <Alert severity="warning" sx={{ py: 0, px: 1, mb: 1, '& .MuiAlert-message': { py: 0.5 } }}>
                        <Typography variant="caption">Set alarm! You won't feel hungry.</Typography>
                      </Alert>
                    )}

                    {/* Slot Content */}
                    {slotMeal ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {slotMeal.description}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {slotMeal.protein != null && (
                            <Chip size="small" label={`${slotMeal.protein}g protein`} sx={{ backgroundColor: '#ecfdf5', color: '#059669', fontWeight: 600, fontSize: '0.7rem' }} />
                          )}
                          {slotMeal.calories != null && (
                            <Chip size="small" label={`${slotMeal.calories} cal`} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          )}
                          {slotMeal.completed && (
                            <Chip size="small" label="Done" color="success" sx={{ fontSize: '0.7rem' }} />
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" color="text.disabled" fontSize="0.8rem" sx={{ mb: 0.5 }}>
                          Not logged yet
                        </Typography>
                        {slotConfig.notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.3, display: 'block' }}>
                            {slotConfig.notes}
                          </Typography>
                        )}
                        {/* Quick recipe button for any slot */}
                        {['breakfast', 'lunch', 'dinner', 'snack'].includes(slotConfig.slot) && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setCurrentTab(2)}
                            sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.75rem' }}
                            startIcon={<ChefHat size={14} />}
                          >
                            Pick a recipe
                          </Button>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* ==================== TAB 1: WEEKLY VIEW ==================== */}
      <TabPanel value={currentTab} index={1}>
        <WeekNavigation
          currentWeek={selectedWeek}
          onWeekChange={setSelectedWeek}
          title="Weekly Nutrition"
        />

        <Grid container spacing={2}>
          {weeklyMealData.map((day) => {
            const proteinColor = getProteinColor(day.protein, proteinTarget.min);
            return (
              <Grid item xs={12} key={day.dateString}>
                <Card
                  sx={{
                    border: day.isToday ? '2px solid' : '1px solid',
                    borderColor: day.isToday ? 'primary.main' : '#e2e8f0',
                    backgroundColor: day.isToday ? 'rgba(99, 102, 241, 0.04)' : 'transparent',
                  }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      {/* Day info */}
                      <Box sx={{ minWidth: 120 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {day.dayName}
                          </Typography>
                          {day.isToday && <Chip size="small" label="Today" color="primary" sx={{ height: 20 }} />}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {format(day.date, 'MMM dd')}
                        </Typography>
                      </Box>

                      {/* Mode badge */}
                      <Chip
                        size="small"
                        label={day.mode === 'weekday' ? 'Weekday' : 'Weekend'}
                        variant="outlined"
                        color={day.mode === 'weekday' ? 'primary' : 'secondary'}
                        sx={{ height: 22 }}
                      />

                      {/* Protein */}
                      <Box sx={{ minWidth: 100 }}>
                        <Typography variant="caption" color="text.secondary">Protein</Typography>
                        <Typography variant="body2" fontWeight="bold" color={day.protein > 0 ? proteinColor : 'text.disabled'}>
                          {day.protein > 0 ? `${day.protein}g` : '--'}
                        </Typography>
                      </Box>

                      {/* Calories */}
                      <Box sx={{ minWidth: 80 }}>
                        <Typography variant="caption" color="text.secondary">Calories</Typography>
                        <Typography variant="body2" fontWeight="bold" color={day.calories > 0 ? 'text.primary' : 'text.disabled'}>
                          {day.calories > 0 ? day.calories : '--'}
                        </Typography>
                      </Box>

                      {/* Completion */}
                      <Box sx={{ minWidth: 80 }}>
                        <Typography variant="caption" color="text.secondary">Meals</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {day.completed}/{day.totalSlots}
                        </Typography>
                      </Box>

                      {/* Meal chips */}
                      <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ flex: 1 }}>
                        {day.dayMeals.map((meal) => (
                          <Chip
                            key={meal.id}
                            size="small"
                            label={getMealSlotLabel(meal.mealType)}
                            sx={{
                              backgroundColor: meal.completed ? '#ecfdf5' : '#f1f5f9',
                              color: meal.completed ? '#059669' : 'text.secondary',
                              fontSize: '0.65rem',
                              height: 22,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* ==================== TAB 2: RECIPES & REFERENCE ==================== */}
      <TabPanel value={currentTab} index={2}>
        {/* Phase info */}
        <Card sx={{ mb: 3, backgroundColor: '#f8fafc' }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Current Phase: {phase.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Protein: {phase.proteinMin}-{phase.proteinMax}g/day &middot; Weekday: ~{phase.weekdayCalories} cal &middot; Weekend: ~{phase.weekendCalories} cal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Focus:</strong> {phase.focus}
            </Typography>
          </CardContent>
        </Card>

        {/* Search & Filters */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search recipes by name or ingredient..."
          value={recipeSearch}
          onChange={(e) => setRecipeSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />

        {/* Category filter chips */}
        <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '32px', mr: 0.5 }}>
            Meal:
          </Typography>
          {(['breakfast', 'lunch', 'dinner', 'snack'] as MealCategory[]).map((cat) => (
            <Chip
              key={cat}
              size="small"
              label={cat.charAt(0).toUpperCase() + cat.slice(1)}
              variant={recipeCategoryFilter.includes(cat) ? 'filled' : 'outlined'}
              color={recipeCategoryFilter.includes(cat) ? 'primary' : 'default'}
              onClick={() => setRecipeCategoryFilter(prev =>
                prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
              )}
              sx={{ cursor: 'pointer' }}
            />
          ))}
          {recipeCategoryFilter.length > 0 && (
            <Chip size="small" label="Clear" variant="outlined" onClick={() => setRecipeCategoryFilter([])} sx={{ cursor: 'pointer' }} />
          )}
        </Box>

        {/* Prep time & sort */}
        <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '32px', mr: 0.5 }}>
            Time:
          </Typography>
          {[{ label: '≤5 min', value: 5 }, { label: '≤10 min', value: 10 }, { label: '≤15 min', value: 15 }].map(({ label, value }) => (
            <Chip
              key={value}
              size="small"
              label={label}
              variant={recipePrepTimeMax === value ? 'filled' : 'outlined'}
              color={recipePrepTimeMax === value ? 'primary' : 'default'}
              onClick={() => setRecipePrepTimeMax(recipePrepTimeMax === value ? null : value)}
              sx={{ cursor: 'pointer' }}
            />
          ))}

          <Box sx={{ ml: 'auto' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={recipeSortBy}
                onChange={(e) => setRecipeSortBy(e.target.value as typeof recipeSortBy)}
                displayEmpty
                sx={{ fontSize: '0.85rem' }}
              >
                <MenuItem value="name">Sort: Name</MenuItem>
                <MenuItem value="protein">Sort: Protein ↓</MenuItem>
                <MenuItem value="calories">Sort: Calories ↑</MenuItem>
                <MenuItem value="prepTime">Sort: Fastest</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Tag filter chips */}
        <Box display="flex" gap={0.5} flexWrap="wrap" mb={2}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '32px', mr: 0.5 }}>
            Tags:
          </Typography>
          {allTags.map((tag) => (
            <Chip
              key={tag}
              size="small"
              label={tag}
              variant={recipeTagFilter.includes(tag) ? 'filled' : 'outlined'}
              color={recipeTagFilter.includes(tag) ? 'secondary' : 'default'}
              onClick={() => setRecipeTagFilter(prev =>
                prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
              )}
              sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
            />
          ))}
          {recipeTagFilter.length > 0 && (
            <Chip size="small" label="Clear tags" variant="outlined" onClick={() => setRecipeTagFilter([])} sx={{ cursor: 'pointer', fontSize: '0.7rem' }} />
          )}
        </Box>

        {/* Result count */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {filteredRecipes.length} recipe{filteredRecipes.length !== 1 ? 's' : ''}
          {(recipeSearch || recipeCategoryFilter.length > 0 || recipeTagFilter.length > 0 || recipePrepTimeMax !== null)
            ? ` matching filters`
            : ` — breakfast, lunch, dinner & snacks`}
        </Typography>

        {/* Recipe Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {filteredRecipes.map((recipe) => (
            <Grid item xs={12} sm={6} md={4} key={recipe.id}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: 3 },
                  border: expandedRecipe === recipe.id ? '2px solid' : '1px solid',
                  borderColor: expandedRecipe === recipe.id ? 'primary.main' : '#e2e8f0',
                }}
                onClick={() => setExpandedRecipe(expandedRecipe === recipe.id ? null : recipe.id)}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    {recipe.mealCategories.map((cat) => (
                      <Chip
                        key={cat}
                        size="small"
                        label={cat}
                        sx={{
                          fontSize: '0.6rem',
                          height: 18,
                          backgroundColor: cat === 'breakfast' ? '#eef2ff' : cat === 'lunch' ? '#faf5ff' : cat === 'dinner' ? '#fef2f2' : '#ecfdf5',
                          color: cat === 'breakfast' ? '#6366f1' : cat === 'lunch' ? '#7c3aed' : cat === 'dinner' ? '#ef4444' : '#059669',
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {recipe.name}
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                    <Chip size="small" label={`${recipe.protein}g protein`} sx={{ backgroundColor: '#ecfdf5', color: '#059669', fontSize: '0.7rem' }} />
                    <Chip size="small" label={`~${recipe.calories} cal`} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    {recipe.prepTime > 0 && <Chip size="small" label={`${recipe.prepTime} min`} variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                    {recipe.prepTime === 0 && <Chip size="small" label="No prep" variant="outlined" sx={{ fontSize: '0.7rem', color: '#059669' }} />}
                  </Box>

                  <Collapse in={expandedRecipe === recipe.id}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.5}>
                      Ingredients:
                    </Typography>
                    {recipe.ingredients.map((ing, i) => (
                      <Typography key={i} variant="caption" color="text.secondary" display="block">
                        &bull; {ing}
                      </Typography>
                    ))}
                    <Alert severity="info" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                      <Typography variant="caption">{recipe.tip}</Typography>
                    </Alert>
                    {recipe.tags.length > 0 && (
                      <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                        {recipe.tags.map(tag => (
                          <Chip key={tag} size="small" label={tag} variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                        ))}
                      </Box>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      fullWidth
                      sx={{ mt: 1, textTransform: 'none' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUseRecipe(recipe);
                      }}
                    >
                      Use This Recipe
                    </Button>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredRecipes.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">No recipes match your filters.</Typography>
            <Button
              size="small"
              onClick={() => {
                setRecipeSearch('');
                setRecipeCategoryFilter([]);
                setRecipeTagFilter([]);
                setRecipePrepTimeMax(null);
              }}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              Clear all filters
            </Button>
          </Box>
        )}

        {/* Protein Reference */}
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Protein Reference
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Quick lookup for hitting your daily target
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                <TableCell><strong>Food</strong></TableCell>
                <TableCell align="right"><strong>Protein</strong></TableCell>
                <TableCell><strong>Best For</strong></TableCell>
                <TableCell><strong>Effort</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PROTEIN_REFERENCE.map((item, i) => (
                <TableRow key={i} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f8fafc' } }}>
                  <TableCell>
                    <Typography variant="body2">{item.food}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="#6366f1">
                      {item.protein}g
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{item.bestTiming}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{item.prepEffort}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* All Phases Overview */}
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Nutrition Phases
        </Typography>
        <Grid container spacing={2}>
          {NUTRITION_PHASES.map((p) => (
            <Grid item xs={12} sm={6} key={p.id}>
              <Card
                variant="outlined"
                sx={{
                  border: p.id === phase.id ? '2px solid' : '1px solid',
                  borderColor: p.id === phase.id ? 'primary.main' : '#e2e8f0',
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {p.name}
                    </Typography>
                    {p.id === phase.id && <Chip size="small" label="Current" color="primary" sx={{ height: 20 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Weeks {p.weekStart}-{p.weekEnd || 'ongoing'} &middot; Protein: {p.proteinMin}-{p.proteinMax}g
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Weekday: ~{p.weekdayCalories} cal &middot; Weekend: ~{p.weekendCalories} cal
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    <strong>Key:</strong> {p.keyChange}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* ==================== TAB 3: QUICK ENTRY ==================== */}
      <TabPanel value={currentTab} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Plus size={24} color="#6366f1" />
                  <Typography variant="h6">Log a Meal</Typography>
                </Box>

                <Box component="form" onSubmit={handleQuickEntry}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Meal Slot</InputLabel>
                    <Select
                      value={qeSlot}
                      onChange={(e) => setQeSlot(e.target.value as MealSlot)}
                      label="Meal Slot"
                    >
                      <MenuItem value="breakfast">Breakfast</MenuItem>
                      <MenuItem value="mid-morning-snack">Mid-Morning Snack</MenuItem>
                      <MenuItem value="lunch">Lunch</MenuItem>
                      <MenuItem value="pre-ride-snack">Pre-Ride Snack</MenuItem>
                      <MenuItem value="post-ride-snack">Post-Ride Snack</MenuItem>
                      <MenuItem value="dinner">Dinner</MenuItem>
                      <MenuItem value="snack">Snack (Other)</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={2}
                    value={qeDescription}
                    onChange={(e) => setQeDescription(e.target.value)}
                    required
                    sx={{ mb: 2 }}
                    placeholder="What did you eat?"
                  />

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Protein (g)"
                        type="number"
                        value={qeProtein}
                        onChange={(e) => setQeProtein(e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Calories"
                        type="number"
                        value={qeCalories}
                        onChange={(e) => setQeCalories(e.target.value)}
                        inputProps={{ min: 0, step: 10 }}
                      />
                    </Grid>
                  </Grid>

                  {/* Quick protein chips */}
                  <Box display="flex" gap={0.5} flexWrap="wrap" mb={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
                      Quick add protein:
                    </Typography>
                    {[10, 15, 20, 25, 30, 35, 40, 50].map((g) => (
                      <Chip
                        key={g}
                        size="small"
                        label={`+${g}g`}
                        onClick={() => setQeProtein(prev => String((parseInt(prev) || 0) + g))}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#ecfdf5' } }}
                      />
                    ))}
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={!qeDescription || qeLoading}
                    sx={{ py: 1.5 }}
                  >
                    {qeLoading ? 'Adding...' : 'Log Meal'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Today's Meals
                </Typography>
                {todayMeals.length === 0 ? (
                  <Typography color="text.secondary">No meals logged today.</Typography>
                ) : (
                  todayMeals.map((meal, index) => (
                    <React.Fragment key={meal.id}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" py={1}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Chip
                              size="small"
                              label={getMealSlotLabel(meal.mealType)}
                              sx={{
                                backgroundColor: getMealSlotColor(meal.mealType),
                                color: 'white',
                                fontSize: '0.7rem',
                              }}
                            />
                            {meal.completed && <CheckCircle size={14} color="#059669" />}
                          </Box>
                          <Typography variant="body2">{meal.description}</Typography>
                          <Box display="flex" gap={1}>
                            {meal.protein != null && (
                              <Typography variant="caption" color="#059669" fontWeight="bold">
                                {meal.protein}g protein
                              </Typography>
                            )}
                            {meal.calories != null && (
                              <Typography variant="caption" color="text.secondary">
                                {meal.calories} cal
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box display="flex" gap={0.5}>
                          <Button
                            size="small"
                            variant={meal.completed ? 'contained' : 'outlined'}
                            color={meal.completed ? 'success' : 'primary'}
                            onClick={() => handleToggleCompleted(meal)}
                          >
                            {meal.completed ? 'Done' : 'Mark Done'}
                          </Button>
                          {onDeleteMeal && (
                            <IconButton size="small" onClick={() => handleDeleteMeal(meal)} color="error">
                              <Trash2 size={16} />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      {index < todayMeals.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Recent meals */}
            {meals.filter(m => m.date !== todayStr).length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Recent Meals</Typography>
                  {[...meals]
                    .filter(m => m.date !== todayStr)
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .slice(0, 8)
                    .map((meal, index) => (
                      <Box key={meal.id} py={0.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            size="small"
                            label={getMealSlotLabel(meal.mealType)}
                            sx={{
                              backgroundColor: getMealSlotColor(meal.mealType),
                              color: 'white',
                              fontSize: '0.65rem',
                              height: 20,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(meal.date + 'T12:00:00'), 'MMM dd')}
                          </Typography>
                          {meal.protein != null && (
                            <Typography variant="caption" color="#059669" fontWeight="bold">
                              {meal.protein}g
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" fontSize="0.8rem" sx={{ ml: 0.5 }}>
                          {meal.description}
                        </Typography>
                      </Box>
                    ))}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* ==================== EDIT DIALOG ==================== */}
      <Dialog open={!!editingDay} onClose={() => setEditingDay(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDay && (
            `${editDescription ? 'Edit' : 'Add'} ${getMealSlotLabel(editingDay.slot)} - ${format(new Date(editingDay.dateString + 'T12:00:00'), 'EEEE, MMM dd')}`
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={2}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            sx={{ mt: 1 }}
            placeholder="What did you eat?"
            autoFocus
          />
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Protein (g)"
                type="number"
                value={editProtein}
                onChange={(e) => setEditProtein(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Calories"
                type="number"
                value={editCalories}
                onChange={(e) => setEditCalories(e.target.value)}
                inputProps={{ min: 0, step: 10 }}
              />
            </Grid>
          </Grid>
          {/* Quick protein chips in dialog */}
          <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
            {[10, 15, 20, 25, 30, 35, 40, 50].map((g) => (
              <Chip
                key={g}
                size="small"
                label={`+${g}g`}
                onClick={() => setEditProtein(prev => String((parseInt(prev) || 0) + g))}
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#ecfdf5' } }}
              />
            ))}
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingDay(null)} startIcon={<X size={16} />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSlotMeal}
            variant="contained"
            disabled={!editDescription || quickAddLoading}
            startIcon={<Save size={16} />}
          >
            {quickAddLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== RECIPE SLOT PICKER DIALOG ==================== */}
      <Dialog open={!!recipeSlotPicker} onClose={() => setRecipeSlotPicker(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Use as which meal?
        </DialogTitle>
        <DialogContent>
          {recipeSlotPicker && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>{recipeSlotPicker.recipe.name}</strong> fits multiple meal slots. Choose one:
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {recipeSlotPicker.slots.map((slot) => (
                  <Button
                    key={slot}
                    variant="outlined"
                    fullWidth
                    onClick={() => handlePickSlotForRecipe(slot)}
                    sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                    startIcon={<Utensils size={16} />}
                  >
                    {getMealSlotLabel(slot)}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipeSlotPicker(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
